import { Collapse, Tooltip, Flex, Progress } from "antd";
import React, { type FC, useEffect, useMemo, useState } from "react";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import { asNonEmpty } from "../../utils/stringUtils";
import populateRatesDosage from "../../../pages/new-trail/hooks/PopulateRatesDosage";
import infoIcon from "../../../assets/images/info.svg";

import {
  type RatesDosageDataInterface,
  type PlotsTableComponentProps,
  type Replicants,
} from "../../../pages/new-trail/types";

import PlotTable from "./PlotTable";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../utils/translationUtils";

const RepicantsLabel = ({
  data,
}: {
  data: Replicants[];
}): React.ReactElement => <>{`+ ${data.length} replicants`}</>;

const PlotsComponent: FC<PlotsTableComponentProps> = ({ from }) => {
  const {
    ratesDosage,
    newTrialState,
    setRatesDosage,
    activeKeys,
    trialProtocolData,
    trialInfo,
    bioApplicationIndex,
    setBioApplicationIndex,
  } = useNewTrialState();

  const { t } = useTranslation();

  const [displayPercent, setDisplayPercent] = useState(0);

  const measureUnitFromTrialInfo =
    trialInfo?.trial_plots?.[0]?.properties?.seeds?.measure_unit ?? null;

  const measureUnitFromProtocol =
    trialProtocolData?.product_details?.seeds?.measure_unit;

  const biologicalData = trialProtocolData?.product_details?.biologicals;

  const totalApplications = Math.max(
    biologicalData?.applications?.length ?? 0,
    1
  );

  const unit =
    asNonEmpty(ratesDosage?.unit_of_rate) ??
    asNonEmpty(measureUnitFromTrialInfo) ??
    asNonEmpty(measureUnitFromProtocol) ??
    "seeds/ha";

  useEffect(() => {
    setRatesDosage((prevRatesDosage) =>
      populateRatesDosage(
        newTrialState?.trial_plots || trialInfo?.trial_plots,
        activeKeys,
        prevRatesDosage?.rates_and_dosages || [],
        asNonEmpty(prevRatesDosage?.unit_of_rate) ?? unit,
        prevRatesDosage?.standard_seed_rate,
        prevRatesDosage?.standard_product,
        prevRatesDosage?.standard_seed_product_id,
        prevRatesDosage?.standard_fertilisers,
        prevRatesDosage?.standard_biological_application_settings,
        prevRatesDosage?.standard_tank_mix_rates
      )
    );
  }, [
    activeKeys,
    newTrialState?.trial_plots,
    trialInfo?.trial_plots,
    unit,
    setRatesDosage,
  ]);

  const trialPlots = newTrialState?.trial_plots || trialInfo?.trial_plots;

  const ratesAndDosesData = ratesDosage?.rates_and_dosages;

  const vaildateField = (data: RatesDosageDataInterface): any => {
    const errors = { variety: "", range: "" };

    const hasVariety =
      typeof data?.variety === "string" && data.variety.trim() !== "";

    const hasRate =
      typeof data?.rate === "number" &&
      !Number.isNaN(data.rate) &&
      data.rate > 0;

    if (!hasVariety) {
      errors.variety = formatTranslation(
        t("trials.plotDesign.trialPlots.errorText.variety")
      );
    }

    if (!hasRate) {
      errors.range = formatTranslation(
        t("trials.plotDesign.trialPlots.errorText.rate")
      );
    }

    if (!hasVariety || !hasRate) return errors;

    const seeds = trialProtocolData?.product_details?.seeds;

    const products = seeds?.products ?? [];

    const selectedProduct =
      products.find((product: any) => product?.variety === data?.variety) ??
      null;

    if (!selectedProduct) {
      return errors;
    }

    const isGlobal = seeds?.global_density_range === true;

    const protocolMeasureUnit = seeds?.measure_unit;

    const selectedUnit = ratesDosage?.unit_of_rate;

    const convertValueIfNeeded = (value: number): number => {
      if (!protocolMeasureUnit || !selectedUnit) return value;

      if (protocolMeasureUnit === selectedUnit) return value;

      if (selectedUnit === "seeds/m2" && protocolMeasureUnit === "seeds/ha") {
        return value / 10000;
      }

      if (selectedUnit === "seeds/ha" && protocolMeasureUnit === "seeds/m2") {
        return value * 10000;
      }

      return value;
    };

    const rangeSource = isGlobal
      ? seeds?.density_range
      : selectedProduct?.density_range;

    if (rangeSource?.min != null && rangeSource?.max != null) {
      const min = convertValueIfNeeded(Number(rangeSource.min));

      const max = convertValueIfNeeded(Number(rangeSource.max));

      if (data.rate !== undefined && data.rate >= min && data.rate <= max) {
        errors.range = "";
      } else {
        errors.range = `Range for selected product is ${String(
          min
        )} to ${String(max)}`;
      }

      return errors;
    }

    errors.range = "";

    return errors;
  };

  const flattenedRates = ratesDosage?.rates_and_dosages?.flat().map((item) => {
    return {
      ...item,
      errors: vaildateField(item),
    };
  });

  const handleApplication = (i: number): void => {
    setBioApplicationIndex(i);
  };

  const biologicalValidation = useMemo(() => {
    if (from !== "biologicals") {
      return {
        hasAtLeastOneTreated: true,
        hasIncompleteTreatments: false,
        isValid: true,
      };
    }

    if (!trialProtocolData?.protocol_type?.includes("biological")) {
      return {
        hasAtLeastOneTreated: true,
        hasIncompleteTreatments: false,
        isValid: true,
      };
    }

    // Every entry is a plot group (an array); the progress effect below reads
    // `plotArray.length` / `.reduce` unguarded, so guarding only here would be
    // inconsistent rather than protective.
    const allPlots = ratesAndDosesData?.flat() ?? [];

    const hasAtLeastOneTreated = allPlots.some((plot: any) => {
      const treatment = plot?.bioTreatement?.[bioApplicationIndex];

      return treatment?.treated === true;
    });

    const hasIncompleteTreatments = allPlots.some((plot: any) => {
      const treatment = plot?.bioTreatement?.[bioApplicationIndex];

      return (
        treatment?.treated === true &&
        (!Array.isArray(treatment?.selected_treatment) ||
          treatment.selected_treatment.length === 0)
      );
    });

    return {
      hasAtLeastOneTreated,
      hasIncompleteTreatments,
      isValid: hasAtLeastOneTreated && !hasIncompleteTreatments,
    };
  }, [
    from,
    ratesAndDosesData,
    trialProtocolData?.protocol_type,
    bioApplicationIndex,
  ]);

  const hasIncompleteTreatments = !biologicalValidation.isValid;

  useEffect(() => {
    const totalPlots =
      ratesAndDosesData?.reduce(
        (count: number, plotArray: any[]) => count + plotArray.length,
        0
      ) ?? 1;

    let validPlots = 0;

    for (let appIdx = 0; appIdx <= bioApplicationIndex; appIdx++) {
      validPlots +=
        ratesAndDosesData?.reduce((count: number, plotArray: any[]) => {
          return (
            count +
            plotArray.reduce((plotCount: number, plot: any) => {
              const treatment = plot?.bioTreatement?.[appIdx];

              if (!treatment?.treated) return plotCount + 1;

              if (
                Array.isArray(treatment?.selected_treatment) &&
                treatment?.selected_treatment.length > 0
              ) {
                return plotCount + 1;
              }

              return plotCount;
            }, 0)
          );
        }, 0) ?? 0;
    }

    const percent = (validPlots / (totalPlots * totalApplications)) * 100;

    setDisplayPercent(percent);
  }, [bioApplicationIndex, totalApplications, ratesAndDosesData]);

  const totalProductPerApplication = ratesAndDosesData?.reduce(
    (total, plotArray) =>
      total +
      plotArray.reduce(
        (plotTotal, plot) =>
          plotTotal +
          (plot?.bioTreatement?.[bioApplicationIndex]?.treated
            ? biologicalData?.products?.[0]?.rate ?? 0
            : 0),
        0
      ),
    0
  );

  return (
    <>
      <div
        className={
          from === "biologicals"
            ? "biologicals-std standard-seedrate-main"
            : "plots-collapse-main"
        }
      >
        {from === "biologicals" && (
          <>
            <div className="seedrate-header">
              <p className="seedrate-heading" style={{ marginBottom: "8px" }}>
                {formatTranslation(
                  t("trials.plotDesign.trialPlots.plotTreatment")
                )}
              </p>

              <Tooltip
                placement="right"
                title={formatTranslation(
                  t("trials.plotDesign.StandardFertilizerRate.tooltip")
                )}
              >
                <img
                  src={infoIcon}
                  alt="info-icon"
                  className="seedrate-info-icon"
                />
              </Tooltip>
            </div>

            <Flex vertical gap={8}>
              <Flex>
                {biologicalData?.applications?.map((data, index) => {
                  const applicationKey =
                    data?.description ?? `application-${index + 1}`;

                  return (
                    <button
                      key={applicationKey}
                      type="button"
                      onClick={() => {
                        handleApplication(index);
                      }}
                      className={
                        "application-name" +
                        (bioApplicationIndex === index ? " isActiveApp" : "")
                      }
                      style={{
                        borderTopLeftRadius: index === 0 ? 4 : 0,
                        borderBottomLeftRadius: index === 0 ? 4 : 0,
                        borderTopRightRadius:
                          index === biologicalData?.applications?.length - 1
                            ? 4
                            : 0,
                        borderBottomRightRadius:
                          index === biologicalData?.applications?.length - 1
                            ? 4
                            : 0,
                      }}
                    >
                      {`${index + 1}${["st", "nd", "rd"][index] || "th"}`}
                    </button>
                  );
                })}
              </Flex>

              <div className="application-info">
                {
                  biologicalData?.applications?.[bioApplicationIndex]
                    ?.description
                }
              </div>
            </Flex>
          </>
        )}

        {trialPlots?.map((plotsItem: any, index: number) => (
          <div className="collpase-one cp-biological" key={plotsItem?.plot?.id}>
            <Collapse
              defaultActiveKey={plotsItem?.plot?.id}
              className="ant-collapse-item"
              expandIconPosition="start"
              items={[
                {
                  key: plotsItem?.plot?.id,
                  label: (
                    <div className="plots-col">
                      <h4>
                        {trialPlots?.some((plot: any) => plot.plot?.isStripPlot)
                          ? "Strip"
                          : formatTranslation(t("plotDetails.plotGroup"))}{" "}
                        {trialPlots?.some((plot: any) => plot.plot?.isStripPlot)
                          ? ""
                          : String.fromCodePoint(65 + index) + " "}
                      </h4>

                      {plotsItem?.replicants?.length ? (
                        <RepicantsLabel data={plotsItem.replicants} />
                      ) : (
                        <></>
                      )}
                    </div>
                  ),
                  children: (
                    <PlotTable
                      ratesAndDoses={ratesAndDosesData?.[index]}
                      productDetails={
                        trialProtocolData?.product_details?.[from]
                      }
                      flattenedRates={flattenedRates}
                      vaildateField={vaildateField}
                      from={from}
                      plotKey={index}
                    />
                  ),
                },
              ]}
            />
          </div>
        ))}

        {from === "biologicals" && (
          <>
            {!biologicalValidation.hasAtLeastOneTreated && (
              <div className="plot-treatment-validation-error">
                {formatTranslation(
                  t(
                    "newTrial.ratesDosage.validation.atLeastOnePlotOrStripTreated"
                  )
                )}
              </div>
            )}

            {biologicalValidation.hasAtLeastOneTreated &&
              biologicalValidation.hasIncompleteTreatments && (
                <div className="plot-treatment-validation-error">
                  {formatTranslation(
                    t(
                      "newTrial.ratesDosage.validation.selectTreatmentForTreatedPlot"
                    )
                  )}
                </div>
              )}

            <Flex
              className="application-step"
              align="center"
              justify="space-between"
              gap={8}
            >
              <Flex gap={8} justify="space-between" align="center">
                <Progress
                  type="circle"
                  percent={Number.parseFloat(displayPercent?.toFixed(1))}
                  size={60}
                />

                <p className="application-step-count">
                  Application {bioApplicationIndex + 1} of {totalApplications}
                </p>
              </Flex>

              {bioApplicationIndex + 1 < totalApplications && (
                <button
                  className={`application-step-btn ${
                    hasIncompleteTreatments
                      ? "application-step-btn-disabled"
                      : ""
                  }`}
                  type="button"
                  // No `hasIncompleteTreatments` guard inside: the button is
                  // disabled on that same flag, so this cannot fire while
                  // treatments are incomplete.
                  onClick={() => {
                    setBioApplicationIndex((prev) =>
                      Math.min(prev + 1, totalApplications - 1)
                    );
                  }}
                  disabled={hasIncompleteTreatments}
                >
                  {formatTranslation(t("createProtocol.summary.nextApp"))}
                </button>
              )}
            </Flex>
          </>
        )}
      </div>

      {from === "biologicals" && (
        <div
          className="product-info-label"
          style={{ padding: "0 16px", display: "none" }}
        >
          {formatTranslation(
            t("trials.plotDesign.trialPlots.plotTotalApplication")
          )}
          : {totalProductPerApplication} L
        </div>
      )}
    </>
  );
};

export default PlotsComponent;
